import React, { useState, useEffect } from "react";
import { paper, Point, Size, Rectangle, Path, PointText } from "paper";
import Footer from "../../layout/Footer";
import Header from "../../layout/Header";
import DownloadMenu from "../../shared/DownloadMenu";
import { MULTIPLIER, PADDING } from "../utils/constants";
import { toRadians, toDegree } from "../utils/mathUtils";
import { drawTunnelHeader } from "../../shared/tunnelHeader";

/**
 * Debug Output Component for Computing View
 * Calculates and displays discontinuity orientation from P2, P4, P5 values
 */
const Debugoutput = (props) => {
  const multiplier = MULTIPLIER;
  const padding = PADDING;
  let tralength = 80;
  const D = parseInt(props.dimensions.direction);
  const P2 = parseFloat(props.dimensions.p2);
  const P4 = parseFloat(props.dimensions.p4);
  const P5 = parseFloat(props.dimensions.p5);

  // Chainage span of the FULL trace, including the invert crossings. The inverts
  // sit |Pmid - P5| beyond the spring lines on the side away from the crown (P5),
  // i.e. at Pmid ± |Pmid - P5| (Pmid = spring-line midpoint). Sizing the log from
  // the spring/crown chainages alone clipped the inverts — amendment 04-Jul-26:
  // "centreline invert value considered for start/end RD".
  const PmidSpan = (P2 + P4) / 2;
  const invertExt = Math.abs(PmidSpan - P5);
  const spanMin = Math.min(P2, P4, P5, PmidSpan - invertExt, PmidSpan + invertExt);
  const spanMax = Math.max(P2, P4, P5, PmidSpan - invertExt, PmidSpan + invertExt);
  if (spanMin < 40) {
    tralength = spanMin > 0 ? spanMin * 2 : 0;
  }
  const length = (parseInt(spanMax - spanMin) + tralength) * multiplier;
  const r = props.dimensions.radiusofcrown * multiplier;
  const height = r * 2 * multiplier;
  const width = r * 2 * multiplier;
  const hw = r * 2 * multiplier;
  const WS = (r * 2) / multiplier;
  const HC = r / multiplier;
  const Theta = 0;
  const Arc = (Math.PI * r) / 2;
  const ArcFull = Arc * 2;
  let Ymax = padding + 2 * (Arc + Arc);
  let text;
  let i = 0;
  let scalearr = [];
  const tracedimensions = props.dimensions;

  const [uservariables, setUserVariables] = useState([]);

  useEffect(() => {
    let TanAlpha, Pmid, Beta, ApDir, Dir, Alpha;

    if (P2 > P4) {
      TanAlpha = WS / (P2 - P4);
      Pmid = P4 + (P2 - P4) / 2;
      Beta = toDegree(Math.atan(HC / Math.abs(Pmid - P5)));
    } else {
      TanAlpha = WS / (P4 - P2);
      Pmid = P2 + (P4 - P2) / 2;
      Beta = toDegree(Math.atan(HC / Math.abs(P5 - Pmid)));
    }
    Alpha = toDegree(Math.atan(TanAlpha));

    const Dopp = D > 180 ? D - 180 : D + 180;
    if (Pmid > P5) {
      ApDir = D;
    } else {
      ApDir = Dopp;
    }

    // Reverse the Tunnel-Inclination correction applied during plotting.
    const I = parseFloat(tracedimensions.inclination) || 0;
    let BetaH = Beta;
    let ApDirOrig = ApDir;
    if (I !== 0) {
      if (ApDir === Dopp) {
        if (Beta < I) {
          BetaH = I - Beta;
          ApDirOrig = D;
        } else {
          BetaH = Beta - I;
        }
      } else {
        if (Beta < 90 - I) {
          BetaH = Beta + I;
        } else {
          BetaH = 180 - I - Beta;
          ApDirOrig = Dopp;
        }
      }
    }

    if (P2 > P4) {
      Dir = ApDirOrig - (90 - Alpha);
    } else {
      Dir = ApDirOrig + (90 - Alpha);
    }
    if (Dir < 0) {
      Dir = 360 + Dir;
    }
    if (Dir > 360) {
      Dir = Dir - 360;
    }

    const Dirstring = String(Math.round(Dir)).padStart(3, "0");

    const TanAmt = Math.tan(toRadians(BetaH)) / Math.sin(toRadians(Alpha));
    const Amt = toDegree(Math.atan(TanAmt));
    const Amtstring = String(Math.round(Amt)).padStart(2, "0");

    const newUsrvar = {};
    newUsrvar["Discontinuity Dip"] = "N" + Dirstring + "/" + Amtstring;
    newUsrvar["Tunnel Direction"] = D;
    newUsrvar["Tunnel Inclination"] = I;
    newUsrvar["Ch. Right SPL"] = P2;
    newUsrvar["Ch. Left SPL"] = P4;
    newUsrvar["Ch. Centerline"] = P5;
    newUsrvar["Alpha"] = Math.round(Alpha);
    newUsrvar["Beta (on log)"] = Math.round(Beta);
    newUsrvar["Beta (horizontal)"] = Math.round(BetaH);
    setUserVariables(newUsrvar);
  }, []);

  useEffect(() => {
    if (!props.showdebug) {
      paper.setup("paper-canvas");
      creategraph();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.dimensions, props.showdebug]);

  const creategraph = () => {
    paper.project.clear();
    const width = length + 450;
    const height = Math.max(width / 2, Ymax + 120);
    paper.view.viewSize = new Size(width, height);
    draw();
    paper.view.draw();
  };

  const createScale = () => {
    for (i = padding; i <= length + padding; i += multiplier * 10) {
      text = new PointText(new Point(i, Ymax + 15));
      text.justification = "center";
      text.fillColor = "black";
      text.content = Math.round((i - padding) / multiplier + spanMin - tralength / 2);
      scalearr.push([i, Ymax + 10]);
      scalearr.push([i, Ymax]);

      if (i + multiplier * 10 <= length + padding) {
        for (let j = 1; j <= 9; j++) {
          scalearr.push([i + multiplier * j, Ymax]);
          scalearr.push([i + multiplier * j, Ymax + 5]);
          scalearr.push([i + multiplier * j, Ymax]);
        }
        scalearr.push([i + multiplier * 10, Ymax]);
      }
    }
    new Path({
      segments: scalearr,
      strokeColor: "black",
      strokeWidth: 1,
    });
    if (tracedimensions.iscrown) {
      Ymax = padding + 2 * (hw + Arc);
    }
  };

  const createLog = () => {
    const style = {
      fillColor: "white",
      strokeColor: "black",
      strokeWidth: 1,
    };
    const path = new Path.Rectangle(new Rectangle(new Point(padding, padding), new Size(length, 2 * (Arc + Arc))));
    path.style = style;
    const path1 = new Path.Rectangle(new Rectangle(new Point(padding, padding + Arc), new Size(length, 2 * Arc)));
    path1.style = style;

    const path2 = new Path.Circle(path.bounds.rightCenter.add([r, 0]), r);
    path2.style = style;
    const path3 = new Path.Line(path.bounds.leftCenter, path2.bounds.rightCenter);
    path3.strokeColor = "black";
    path3.dashArray = [4, 10];

    const path4 = new Path.Arc(path.bounds.topRight, path.bounds.rightCenter.add([2 * r, -r / 2]), path.bounds.rightCenter.add([2 * r, 0]));
    path4.strokeColor = "black";
    path4.dashArray = [14, 5];
    const path5 = new Path.Arc(path.bounds.bottomRight, path.bounds.rightCenter.add([2 * r, r / 2]), path.bounds.rightCenter.add([2 * r, 0]));
    path5.strokeColor = "black";
    path5.dashArray = [14, 5];
    const path6 = new Path.Arc(path1.bounds.topRight, path.bounds.rightCenter.add([r - 3, -r - 3]), path.bounds.rightCenter.add([r, -r]));
    path6.strokeColor = "black";
    path6.dashArray = [5, 5];
    const path7 = new Path.Arc(path1.bounds.bottomRight, path.bounds.rightCenter.add([r - 3, r + 3]), path.bounds.rightCenter.add([r, r]));
    path7.strokeColor = "black";
    path7.dashArray = [5, 5];

    // Labels
    let text = new PointText(path.bounds.topLeft.add([-5, 5]));
    text.justification = "right";
    text.fillColor = "black";
    text.content = "Centreline Invert";
    text = new PointText(path.bounds.bottomLeft.add([-5, 5]));
    text.justification = "right";
    text.fillColor = "black";
    text.content = "Centreline Invert";
    text = new PointText(path1.bounds.topLeft.add([-5, 5]));
    text.justification = "right";
    text.fillColor = "black";
    text.content = "Left Spring Level";
    text = new PointText(path1.bounds.bottomLeft.add([-5, 5]));
    text.justification = "right";
    text.fillColor = "black";
    text.content = "Right Spring Level";

    text = new PointText(path.bounds.leftCenter.add([-5, 5]));
    text.justification = "right";
    text.fillColor = "black";
    text.content = "Centreline Crown";
    drawTunnelHeader({ Path, Point, PointText, bounds: path.bounds, direction: tracedimensions.direction, inclination: tracedimensions.inclination, style });
  };

  const createTrace = () => {
    const style = {
      fillColor: "transparent",
      strokeColor: "black",
      strokeWidth: 1,
    };
    let traceunion = [];
    const H = [];
    const XP = [];
    const YP = [];
    const XP5 = [];
    const YP5 = [];
    let Dopp;
    const scalearr = [];

    // Calculate Beta from P2, P4, P5 (these are post-inclination, as drawn on the log)
    let TanAlpha, Pmid, Beta, ApDir, Dir, Alpha;
    if (P2 > P4) {
      TanAlpha = WS / (P2 - P4);
      Pmid = P4 + (P2 - P4) / 2;
      Beta = toDegree(Math.atan(HC / Math.abs(Pmid - P5)));
    } else {
      TanAlpha = WS / (P4 - P2);
      Pmid = P2 + (P4 - P2) / 2;
      Beta = toDegree(Math.atan(HC / Math.abs(P5 - Pmid)));
    }
    Alpha = toDegree(Math.atan(TanAlpha));

    if (D >= 0 && D <= 180) {
      Dopp = D + 180;
    } else {
      Dopp = D - 180;
    }

    if (Pmid > P5) {
      ApDir = D;
    } else {
      ApDir = Dopp;
    }

    // Reverse the Tunnel-Inclination correction to recover the true Dir.
    // (Beta and ApDir below stay as measured for correct visual trace rendering.)
    const Incl = parseFloat(tracedimensions.inclination) || 0;
    let ApDirOrig = ApDir;
    if (Incl !== 0) {
      if (ApDir === Dopp) {
        if (Beta >= Incl) {
          // no flip
        } else {
          ApDirOrig = D;
        }
      } else {
        if (Beta >= 90 - Incl) {
          ApDirOrig = Dopp;
        }
      }
    }

    if (P2 > P4) {
      Dir = ApDirOrig - (90 - Alpha);
    } else {
      Dir = ApDirOrig + (90 - Alpha);
    }
    if (Dir < 0) {
      Dir = 360 + Dir;
    }
    if (Dir > 360) {
      Dir = Dir - 360;
    }

    // Beta is in degrees, convert to radians for Math.tan
    const TanBeta = Math.tan(toRadians(Beta));
    const XP2 = padding + (P2 * multiplier) - (spanMin - tralength / 2) * multiplier;

    const Diff = D - Dir;
    let XP4, XPmid;

    if (ApDir === D) {
      if ((Diff > -360 && Diff < -270) || (Diff > 0 && Diff < 90)) {
        XP4 = XP2 - (r * 2) / TanAlpha;
        XPmid = XP2 - Math.abs(XP2 - XP4) / 2;
      } else {
        XP4 = XP2 + (r * 2) / TanAlpha;
        XPmid = XP2 + Math.abs(XP2 - XP4) / 2;
      }
    } else {
      // Subroutine 2
      const DiffDopp = Dopp - Dir;
      if ((DiffDopp > -360 && DiffDopp < -270) || (DiffDopp > 0 && DiffDopp < 90)) {
        XP4 = XP2 - (r * 2) / TanAlpha;
        XPmid = XP2 - Math.abs(XP2 - XP4) / 2;
      } else {
        XP4 = XP2 + (r * 2) / TanAlpha;
        XPmid = XP2 + Math.abs(XP2 - XP4) / 2;
      }
    }

    // Right wall
    for (let n = 90; n >= Theta; n--) {
      // n is in degrees, convert to radians for Math.sin
      H[n] = r * Math.sin(toRadians(n));
      if (ApDir === D) {
        if (XP2 > XP4) {
          XP[n] = XP2 - Math.abs(XP2 - XPmid) * ((n - Theta) / (90 - Theta));
        } else {
          XP[n] = XP2 + Math.abs(XP2 - XPmid) * ((n - Theta) / (90 - Theta));
        }
        XP5[n] = XP[n] + H[n] / TanBeta;
        YP[n] = Ymax - (Arc - Arc * ((n - Theta) / (90 - Theta)));
        YP5[n] = YP[n];
      } else {
        // Subroutine 2
        if (XP2 > XP4) {
          XP[n] = XP2 - Math.abs(XP2 - XPmid) * ((n - Theta) / (90 - Theta));
        } else {
          XP[n] = XP2 + Math.abs(XP2 - XPmid) * ((n - Theta) / (90 - Theta));
        }
        YP[n] = Ymax - (Arc - Arc * ((n - Theta) / (90 - Theta)));
        XP5[n] = XP[n] - H[n] / TanBeta;
        YP5[n] = YP[n];
      }
      traceunion.push([XP5[n], YP5[n]]);
    }

    // Right crown
    for (let n = Theta; n <= 90; n++) {
      // n is in degrees, convert to radians for Math.sin
      H[n] = r * Math.sin(toRadians(n));
      if (ApDir === D) {
        if (XP2 > XP4) {
          XP[n] = XP2 - Math.abs(XP2 - XPmid) * ((n - Theta) / (90 - Theta));
        } else {
          XP[n] = XP2 + Math.abs(XP2 - XPmid) * ((n - Theta) / (90 - Theta));
        }
        XP5[n] = XP[n] - H[n] / TanBeta;
        YP[n] = Ymax - (Arc + Arc * ((n - Theta) / (90 - Theta)));
      } else {
        // Subroutine 2
        if (XP2 > XP4) {
          XP[n] = XP2 - Math.abs(XPmid - XP2) * ((n - Theta) / (90 - Theta));
        } else {
          XP[n] = XP2 + Math.abs(XPmid - XP2) * ((n - Theta) / (90 - Theta));
        }
        XP5[n] = XP[n] + H[n] / TanBeta;
        YP[n] = Ymax - (Arc + Arc * ((n - Theta) / (90 - Theta)));
      }
      YP5[n] = YP[n];
      scalearr.push([XP5[n], YP5[n]]);
      traceunion.push([XP5[n], YP5[n]]);
    }

    // Left crown
    scalearr.length = 0;
    for (let n = 90; n >= Theta; n--) {
      // n is in degrees, convert to radians for Math.sin
      H[n] = r * Math.sin(toRadians(n));
      if (ApDir === D) {
        if (XP2 > XP4) {
          XP[n] = XP4 + Math.abs(XP4 - XPmid) * ((n - Theta) / (90 - Theta));
        } else {
          XP[n] = XP4 - Math.abs(XP4 - XPmid) * ((n - Theta) / (90 - Theta));
        }
        XP5[n] = XP[n] - H[n] / TanBeta;
        YP[n] = Ymax - (Arc * 3 - Arc * ((n - Theta) / (90 - Theta)));
      } else {
        // Subroutine 2
        if (XP2 > XP4) {
          XP[n] = XP4 + Math.abs(XP4 - XPmid) * ((n - Theta) / (90 - Theta));
        } else {
          XP[n] = XP4 - Math.abs(XP4 - XPmid) * ((n - Theta) / (90 - Theta));
        }
        XP5[n] = XP[n] + H[n] / TanBeta;
        YP[n] = Ymax - (Arc * 3 - Arc * ((n - Theta) / (90 - Theta)));
      }

      YP5[n] = YP[n];
      scalearr.push([XP5[n], YP5[n]]);
      traceunion.push([XP5[n], YP5[n]]);
    }

    // Left wall
    for (let n = Theta; n <= 90; n++) {
      // n is in degrees, convert to radians for Math.sin
      H[n] = r * Math.sin(toRadians(n));
      if (parseInt(ApDir) === parseInt(D)) {
        if (XP2 > XP4) {
          XP[n] = XP4 + Math.abs(XP2 - XPmid) * ((n - Theta) / (90 - Theta));
        } else {
          XP[n] = XP4 - Math.abs(XP2 - XPmid) * ((n - Theta) / (90 - Theta));
        }
        XP5[n] = XP[n] + H[n] / TanBeta;
        YP[n] = Ymax - (Arc * 3 + Arc * ((n - Theta) / (90 - Theta)));
      } else {
        // Subroutine 2
        if (XP2 > XP4) {
          XP[n] = XP4 + Math.abs(XPmid - XP4) * ((n - Theta) / (90 - Theta));
        } else {
          XP[n] = XP4 - Math.abs(XPmid - XP4) * ((n - Theta) / (90 - Theta));
        }
        XP5[n] = XP[n] - H[n] / TanBeta;
        YP[n] = Ymax - (Arc * 3 + Arc * ((n - Theta) / (90 - Theta)));
      }

      YP5[n] = YP[n];
      traceunion.push([XP5[n], YP5[n]]);
    }

    const union = new Path({
      segments: traceunion,
      style: style,
    });

    // Logic for preventing curve going outside plot
    const start = new Path.Line(new Point(padding, 0), new Point(padding, 600));
    let interx = union.getIntersections(start);

    if (interx.length === 1) {
      const firstoffset = union.getOffsetOf(interx[0].point);
      const firstcut = union.splitAt(firstoffset);
      if (firstcut.segments[1].point.x < padding) {
        firstcut.opacity = 0;
      } else {
        union.opacity = 0;
      }
    }
    if (interx.length === 2) {
      const firstoffset = union.getOffsetOf(interx[0].point);
      const firstcut = union.splitAt(firstoffset);
      const secondoffset = firstcut.getOffsetOf(interx[1].point);
      const secondcut = firstcut.splitAt(secondoffset);

      if (firstcut.segments[2].point.x < padding) {
        firstcut.opacity = 0;
      } else {
        secondcut.opacity = 0;
        union.opacity = 0;
      }
    }
    const end = new Path.Line(new Point(padding + length, 0), new Point(padding + length, 600));
    interx = union.getIntersections(end);
    if (interx.length === 1) {
      const firstoffset = union.getOffsetOf(interx[0].point);
      const firstcut = union.splitAt(firstoffset);
      if (firstcut.segments[1].point.x > padding + length) {
        firstcut.opacity = 0;
      } else {
        union.opacity = 0;
      }
    }
    if (interx.length === 2) {
      const firstoffset = union.getOffsetOf(interx[0].point);
      const firstcut = union.splitAt(firstoffset);
      const secondoffset = firstcut.getOffsetOf(interx[1].point);
      const secondcut = firstcut.splitAt(secondoffset);

      if (firstcut.segments[2].point.x > padding + length) {
        firstcut.opacity = 0;
      } else {
        secondcut.opacity = 0;
        union.opacity = 0;
      }
    }
  };

  const createText = () => {
    // Recompute from P2, P4, P5 (same logic as useEffect)
    let TanAlpha, Pmid, Beta, ApDir, Dir, Alpha;
    if (P2 > P4) {
      TanAlpha = WS / (P2 - P4);
      Pmid = P4 + (P2 - P4) / 2;
      Beta = toDegree(Math.atan(HC / Math.abs(Pmid - P5)));
    } else {
      TanAlpha = WS / (P4 - P2);
      Pmid = P2 + (P4 - P2) / 2;
      Beta = toDegree(Math.atan(HC / Math.abs(P5 - Pmid)));
    }
    Alpha = toDegree(Math.atan(TanAlpha));
    const Dopp = D > 180 ? D - 180 : D + 180;
    ApDir = Pmid > P5 ? D : Dopp;

    // Reverse the Tunnel-Inclination correction to recover true Dir / Amt.
    const Incl = parseFloat(tracedimensions.inclination) || 0;
    let BetaH = Beta;
    let ApDirOrig = ApDir;
    if (Incl !== 0) {
      if (ApDir === Dopp) {
        if (Beta < Incl) {
          BetaH = Incl - Beta;
          ApDirOrig = D;
        } else {
          BetaH = Beta - Incl;
        }
      } else {
        if (Beta < 90 - Incl) {
          BetaH = Beta + Incl;
        } else {
          BetaH = 180 - Incl - Beta;
          ApDirOrig = Dopp;
        }
      }
    }

    Dir = P2 > P4 ? ApDirOrig - (90 - Alpha) : ApDirOrig + (90 - Alpha);
    if (Dir < 0) Dir += 360;
    if (Dir > 360) Dir -= 360;

    const Dirstring = String(Math.round(Dir)).padStart(3, "0");
    const TanAmt = Math.tan(toRadians(BetaH)) / Math.sin(toRadians(Alpha));
    const Amt = toDegree(Math.atan(TanAmt));
    const Amtstring = String(Math.round(Amt)).padStart(2, "0");
    const dipStr = "N" + Dirstring + "/" + Amtstring;

    const inputsY = Ymax + 35;
    const outputsY = Ymax + 73;

    const inputsLabel = new PointText(new Point(padding, inputsY));
    inputsLabel.justification = "left";
    inputsLabel.fillColor = "black";
    inputsLabel.fontWeight = "bold";
    inputsLabel.content = "Inputs:";

    const inputsText = new PointText(new Point(padding + inputsLabel.bounds.width + 4, inputsY));
    inputsText.justification = "left";
    inputsText.fillColor = "black";
    inputsText.content =
      "Tunnel Direction: " + D +
      "; Tunnel Inclination: " + Incl +
      "; Ch. Right SPL: " + P2 +
      "; Ch. Left SPL: " + P4;

    const inputsText2 = new PointText(new Point(padding, inputsY + 18));
    inputsText2.justification = "left";
    inputsText2.fillColor = "black";
    inputsText2.content =
      "Ch. Centerline: " + P5 +
      "; Tunnel Radius: " + tracedimensions.radiusofcrown;

    const outputsLabel = new PointText(new Point(padding, outputsY));
    outputsLabel.justification = "left";
    outputsLabel.fillColor = "black";
    outputsLabel.fontWeight = "bold";
    outputsLabel.content = "Outputs:";

    const outputsText = new PointText(new Point(padding + outputsLabel.bounds.width + 4, outputsY));
    outputsText.justification = "left";
    outputsText.fillColor = "black";
    outputsText.content = "Discontinuity Dip: " + dipStr + " (Dip Direction/ Amount)";

    const cautionY = outputsY + 26;
    const cautionLabel = new PointText(new Point(padding, cautionY));
    cautionLabel.justification = "left";
    cautionLabel.fillColor = "black";
    cautionLabel.fontWeight = "bold";
    cautionLabel.content = "Caution:";

    const cautionText = new PointText(new Point(padding + cautionLabel.bounds.width + 4, cautionY));
    cautionText.justification = "left";
    cautionText.fillColor = "black";
    cautionText.content = "Discontinuity trace span may be evaluated with care in consideration of its persistence.";
  };

  const draw = () => {
    createScale();
    createLog();
    createTrace();
    createText();
  };

  return (
    <div id="page">
      <Header onBack={props.showinput} />
      <Footer />
      <div className="page-content header-clear-medium">
        <div className="card card-style contact-form">
          <div className="content">
            <div className="row">
              <div className="col-md-12">
                <button
                  onClick={() => props.showinput()}
                  className="btn bg-green-dark mw-2 me-3"
                >
                  Edit Input
                </button>
                <DownloadMenu
                  filename="circular-debug"
                  screenLabel="Circular Tunnel — Computing"
                  feedbackInputs={props.dimensions}
                />
              </div>
            </div>
            <div className="row">
              <div className="col-md-12 overflow-auto">
                <canvas id="paper-canvas" resize="true" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Debugoutput;
